import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import resourcesApi from '../../utils/resourcesApi';
import { checkAdminStatus } from '../../utils/adminUtils';
import LoadingScreen from '../../components/LoadingScreen';
import ResourceAdminMenu from '../../components/resources/ResourceAdminMenu';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ResourceDetailPage = () => {
  const { slug } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resource, setResource] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  
  // Fetch resource data
  useEffect(() => {
    const fetchResource = async () => {
      setLoading(true);
      setError(null);
      try {
        const resourceData = await resourcesApi.getResourceBySlug(slug);
        
        setResource(resourceData);
        setIsLiked(resourceData.is_liked_by_user);
        setLikeCount(resourceData.like_count);
      } catch (error) {
        console.error('Error fetching resource:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    // Check if user is admin
    const checkUserAdminStatus = async () => {
      if (user) {
        const isUserAdmin = await checkAdminStatus(user);
        setIsAdmin(isUserAdmin);
      }
    };
    
    fetchResource();
    checkUserAdminStatus();
  }, [slug, user]);
  
  // Handle submitting a comment
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      navigate('/signin', { state: { from: `/resources/${slug}` } });
      return;
    }
    
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const commentData = await resourcesApi.addComment(resource.id, newComment);
      
      // Add the new comment to the list
      setResource(prev => ({
        ...prev,
        comments: [commentData, ...prev.comments]
      }));
      
      // Clear the comment input
      setNewComment('');
    } catch (error) {
      console.error('Error posting comment:', error);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle like/unlike
  const handleLikeToggle = async () => {
    if (!isAuthenticated) {
      navigate('/signin', { state: { from: `/resources/${slug}` } });
      return;
    }
    
    try {
      const likeData = await resourcesApi.toggleLike(resource.id);
      
      setIsLiked(likeData.liked);
      setLikeCount(likeData.like_count);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  
  // Handle audio playback
  const handleAudioToggle = () => {
    if (!resource?.voice) return;
    
    if (audioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    
    setAudioPlaying(!audioPlaying);
  };
  
  if (loading) {
    return <LoadingScreen message="Loading resource..." />;
  }
  
  if (error || !resource) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Error</h1>
        <p className="text-red-600 mb-6">{error || 'Resource not found'}</p>
        <Link
          to="/resources"
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          Back to Resources
        </Link>
      </div>
    );
  }
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <>
      <ResourceAdminMenu />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin controls */}
        {isAdmin && (
          <div className="mb-6 flex justify-end space-x-4">
            <Link
              to={`/resources/edit/${resource.id}`}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit Resource
            </Link>
            <Link
              to={`/resources/voice/${resource.id}`}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              {resource.has_audio ? 'Update Audio' : 'Add Audio'}
            </Link>
          </div>
        )}
        
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <ol className="flex text-sm text-gray-500">
            <li>
              <Link to="/resources" className="hover:text-primary-600">Resources</Link>
            </li>
            <li className="mx-2">/</li>
            {resource.resource_categories ? (
              <>
                <li>
                  <Link 
                    to={`/resources?category=${resource.resource_categories.slug}`} 
                    className="hover:text-primary-600"
                  >
                    {resource.resource_categories.name}
                  </Link>
                </li>
                <li className="mx-2">/</li>
              </>
            ) : null}
            <li className="text-gray-700">{resource.title}</li>
          </ol>
        </nav>
        
        {/* Main content */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Cover image */}
          {resource.featured_image_url && (
            <div className="h-64 md:h-80 overflow-hidden">
              <img
                src={resource.featured_image_url}
                alt={resource.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">{resource.title}</h1>
              
              {/* Audio player */}
              {resource.voice && (
                <div className="flex items-center">
                  <button
                    onClick={handleAudioToggle}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    {audioPlaying ? (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Pause Audio
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Listen
                      </>
                    )}
                  </button>
                  <audio 
                    ref={audioRef} 
                    src={resource.voice.audio_url} 
                    className="hidden"
                    onEnded={() => setAudioPlaying(false)}
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center text-sm text-gray-500 mb-8">
              <span className="mr-4">
                {formatDate(resource.created_at)}
              </span>
              <span className="flex items-center mr-4">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {resource.view_count} views
              </span>
              
              {resource.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 ml-auto">
                  {resource.tags.map(tag => (
                    <Link
                      key={tag.id}
                      to={`/resources?tag=${tag.name}`}
                      className="px-2 py-1 bg-gray-100 text-xs rounded-full hover:bg-gray-200 transition-colors"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Summary</h2>
              <p className="text-gray-700 leading-relaxed">{resource.summary}</p>
            </div>
            
            <div className="prose prose-primary max-w-none mb-8">
              {/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 className="text-2xl font-bold my-4" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-xl font-bold my-3" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-lg font-bold my-3" {...props} />,
                  p: ({node, ...props}) => <p className="my-2" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-5 my-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-5 my-2" {...props} />,
                  li: ({node, ...props}) => <li className="ml-2 my-1" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-200 pl-4 italic my-3" {...props} />,
                  a: ({node, ...props}) => <a className="text-blue-600 hover:underline" {...props} />,
                  code: ({node, inline, ...props}) => 
                    inline ? <code className="bg-gray-100 px-1 rounded" {...props} /> : <pre className="bg-gray-100 p-3 rounded overflow-auto"><code {...props} /></pre>,
                }}
              >
                {resource.content}
              </ReactMarkdown>
              {/* eslint-enable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
            </div>
            
            {/* Social interactions */}
            <div className="flex items-center justify-between py-4 border-t border-b border-gray-200 mb-8">
              <div className="flex items-center space-x-6">
                <button
                  onClick={handleLikeToggle}
                  className={`flex items-center space-x-2 ${
                    isLiked
                      ? 'text-blue-600 hover:text-blue-800'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg 
                    className="w-5 h-5" 
                    fill={isLiked ? "currentColor" : "none"} 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" 
                    />
                  </svg>
                  <span>{likeCount} {likeCount === 1 ? 'Like' : 'Likes'}</span>
                </button>
                
                <button
                  onClick={() => {
                    const commentSection = document.getElementById('comments-section');
                    commentSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <span>{resource.comments.length} {resource.comments.length === 1 ? 'Comment' : 'Comments'}</span>
                </button>
              </div>
              
              <div>
                <button
                  onClick={() => {
                    navigator.share({
                      title: resource.title,
                      text: resource.summary,
                      url: window.location.href
                    }).catch(err => {
                      console.error('Share failed:', err);
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    });
                  }}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span>Share</span>
                </button>
              </div>
            </div>
            
            {/* Comments section */}
            <div id="comments-section" className="mt-10">
              <h2 className="text-xl font-bold mb-6">Comments ({resource.comments.length})</h2>
              
              {/* Comment form */}
              {isAuthenticated ? (
                <form onSubmit={handleCommentSubmit} className="mb-8">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="4"
                    required
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="submit"
                      disabled={submitting || !newComment.trim()}
                      className={`px-6 py-2 rounded-md ${
                        submitting || !newComment.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      {submitting ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 mb-8 text-center">
                  <p className="text-gray-700 mb-4">Please sign in to add a comment</p>
                  <Link 
                    to={`/signin?redirect=/resources/${slug}`}
                    className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Sign In
                  </Link>
                </div>
              )}
              
              {/* Comments list */}
              {resource.comments.length > 0 ? (
                <div className="space-y-6">
                  {resource.comments.map(comment => (
                    <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {comment.user_profiles?.photo_url ? (
                              <img
                                src={comment.user_profiles.photo_url}
                                alt={comment.user_profiles.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-lg text-gray-600">
                                {comment.user_profiles?.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-800">
                              {comment.user_profiles?.name || 'Anonymous'}
                            </h4>
                            <span className="text-sm text-gray-500">
                              {formatDate(comment.created_at)}
                            </span>
                          </div>
                          <p className="mt-2 text-gray-700">{comment.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No comments yet. Be the first to share your thoughts!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourceDetailPage; 